import { cloneDeep } from 'lodash';
// This is the modal that appears when the user clicks the "Measured Window" button in the "Building part details" section
// of the "Main Building" tab after setting "Coverage" to "much more (or less) than typical" on the "Windows" section
// of the "Openings" tab.

import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';

type LookUpCode = {
  lookUpItem: string;
  lookupValue: string;
  dataItem: string;
};

type WindowDetail = {
  openingId: string;
  windowLocation: string;
  windowArea: {
    value: number,
    quantity?: string,
  },
  glazingType: string;
  windowType: any;
  orientation: string;
  pvcFrame: boolean;
  glazingGap: string;
  windowTransmissionDetails: {
    dataSource: number, 
    uValue: number,
    solarTransmittance: number,
  },
}

@Component({
  selector: 'app-measurement-windows-modal',
  templateUrl: './ee-openings-measurement-windows.component.html'
})
export class MeasurementWindowsModalComponent implements OnInit {

  @Input() windowsDetails: WindowDetail[] = [];
  @Input() windowsDetailsForActiveDataSet: any;
  @Input() hasActiveDataSet: boolean;
  @Input() keyedLookUpCodes: any;
  @Input() lookUpCodesByDataItem: any;
  @Input() isEdit: boolean;
  @Input() modifiedBackgroundClass: string;
  @Input() countryCode: string;

  openingsMeasurementWindowsDetails: WindowDetail[] = [];

  @Input() eeRRLookupCodes: LookUpCode[];
  @Input() openingId: string;
  @Input() windowLocation: string;

  orientationOptions: LookUpCode[] = [];
  glazingTypes: LookUpCode[] = [];
  glazingGaps: LookUpCode[] = [];
  windowTransmissionDataSources: LookUpCode[] = [];
  hidden = true;
  isPvcOrGlazingGap = false;

  constructor(
    public activeModal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    this.setGlazingTypeArray();
    this.orientationOptions = this.lookUpCodesByDataItem['Orientation'];
    const notApplicableOrientation = cloneDeep(this.orientationOptions.find(option => option.lookUpItem === 'Horizontal'));
    if (notApplicableOrientation) {
      notApplicableOrientation.lookupValue = '9';
    }

    this.openingsMeasurementWindowsDetails = this.windowsDetails ?? [];
    this.openingsMeasurementWindowsDetails.forEach((detail, key) => {
      detail.windowType = (detail.windowType == '2') ? true : false;
      this.onGlazingTypeChange(key, detail.glazingType);
      this.onPvcFrameChange(key, detail.pvcFrame);
    });

    this.glazingGaps = this.lookUpCodesByDataItem['GlazingGap'];
    this.windowTransmissionDataSources = this.lookUpCodesByDataItem['WindowTransmissionDataSource'];
    // here , update headers on pageload, make sure its set correctly set correctly
    this.updateHeaderVisibility(); 
  }

  private setGlazingTypeArray(): void {
    this.glazingTypes = this.eeRRLookupCodes.filter(lookUpCode => lookUpCode.dataItem === 'Glazing Type');

    if (this.countryCode === 'SCT') {
      this.glazingTypes = this.glazingTypes.filter(x => x.lookUpItem !== 'Double glazed (before 2002)' && x.lookUpItem !== 'Double glazed (2002 or later)');
    } else {
      this.glazingTypes = this.glazingTypes.filter(x => x.lookUpItem !== 'Double glazed (before 2003)' && x.lookUpItem !== 'Double glazed (2003 or later)');
    }

    this.glazingTypes.sort((a, b) => a.lookupValue > b.lookupValue ? 1 : -1);
  }

  private updateHeaderVisibility(): void {
    this.hidden = true;
    this.isPvcOrGlazingGap = false;

    this.openingsMeasurementWindowsDetails.forEach(detail => {
      if (detail.glazingType === '7' || detail.glazingType === '8') {
        this.hidden = false;
      }
      if (detail.glazingType === '1' || detail.glazingType === '3') {
        this.isPvcOrGlazingGap = true;
      }
      if (detail.glazingType !== '1' && detail.glazingType !== '3' ){
        detail.pvcFrame = false;
        detail.glazingGap = null;
      }
    });
    
  }

  getOrientations(roofLight: boolean) {
    if (this.keyedLookUpCodes && this.orientationOptions) {
      if (roofLight) {
        return this.orientationOptions;
      } else {
        return this.orientationOptions.filter(orientationLookUpCode => {
          return orientationLookUpCode.lookUpItem !== 'Horizontal';
        });
      }
    } else {
      return [];
    }
  }

  getGlazingGaps(pvcFrame: boolean) {
    if (this.keyedLookUpCodes && this.glazingGaps) {
      if (pvcFrame) {
        return this.glazingGaps;
      }
    } else {
      return [];
    }
  }

  getWindowsTransmissionDataSource() {
    if (this.keyedLookUpCodes && this.windowTransmissionDataSources) {
      return this.windowTransmissionDataSources;
    } else {
      return [];
    }
  }

  addNew(): void {
    this.openingsMeasurementWindowsDetails.push({
      openingId: (this.openingId) ? this.openingId : (this.openingsMeasurementWindowsDetails.length.toString()),
      windowLocation: this.windowLocation,
      windowArea: {
        value: 0,
        quantity: "m",
      },
      glazingType: null,
      windowType: false,
      orientation: null,
      pvcFrame: null,
      glazingGap: null,
      windowTransmissionDetails: {
        dataSource: null, 
        uValue: 0,
        solarTransmittance: 0,
      }
    });
    // here, update visibility after adding a new entry
    this.updateHeaderVisibility();
  }

  remove(windowsDetail: WindowDetail): void {
    const index = this.openingsMeasurementWindowsDetails.indexOf(windowsDetail);
    if (index > -1) {
      this.openingsMeasurementWindowsDetails.splice(index, 1);
      this.updateHeaderVisibility(); // Update visibility after removing an entry
    }
  }

  highlightDetailAsModified(index: number, detailName: string, currentDetailValue: any): boolean {
    if (!this.hasActiveDataSet) {
      return false;
    }

    const activeDatasetWindowDetailsAtIndex = this.windowsDetailsForActiveDataSet.length > index ? this.windowsDetailsForActiveDataSet[index] : null;
    if (activeDatasetWindowDetailsAtIndex != null) {
      return activeDatasetWindowDetailsAtIndex[detailName] !== currentDetailValue;
    } else {
      return true;
    }
  }

  onPvcFrameChange(openingId, target) {
    if (target?.checked === false) {
      this.openingsMeasurementWindowsDetails[openingId].glazingGap = null;
    }
    this.onGlazingTypeChange(openingId, this.openingsMeasurementWindowsDetails[openingId].glazingType);
  }

  onWindowTypeChange(openingId, target) {
    this.openingsMeasurementWindowsDetails[openingId].windowType = target?.checked;
    this.onGlazingTypeChange(openingId, this.openingsMeasurementWindowsDetails[openingId].glazingType);
  }

  onGlazingGapChange(openingId, value) {
    this.openingsMeasurementWindowsDetails[openingId].glazingGap = value;
    this.onGlazingTypeChange(openingId, this.openingsMeasurementWindowsDetails[openingId].glazingType);
  }

  onGlazingTypeChange(openingId, value) {
    if (openingId >= 0) {
      // Update the details for the specific openingId
      if (value === '1' || value === '3') {
        this.openingsMeasurementWindowsDetails[openingId].windowTransmissionDetails.dataSource = null;
      }
  
      // Update U-value and solar transmittance based on the glazing type
      if (value === '1' || value === '3') {
        const glazingGap = this.openingsMeasurementWindowsDetails[openingId].glazingGap;
        
        if (glazingGap === '6' || !glazingGap || glazingGap === '12') {
          this.openingsMeasurementWindowsDetails[openingId].windowTransmissionDetails.uValue = null;
        }
        
        this.openingsMeasurementWindowsDetails[openingId].windowTransmissionDetails.solarTransmittance = null;
      } else if (['2', '4', '5', '6'].includes(value)) {
        this.openingsMeasurementWindowsDetails[openingId].windowTransmissionDetails.uValue = null;
        this.openingsMeasurementWindowsDetails[openingId].windowTransmissionDetails.solarTransmittance = null;
      }
  
      this.updateHeaderVisibility(); // Update visibility after changing glazing type
    }
  }

  onClose(): void {
    const output = [...this.windowsDetailsForActiveDataSet];
    if (this.openingsMeasurementWindowsDetails) {
      let openingMeasurementsRow;
      this.openingsMeasurementWindowsDetails.forEach((detail) => {
        openingMeasurementsRow = {
          windowLocation: detail.windowLocation || 0,
          glazingType: detail.glazingType || null,
          windowType: detail.windowType || null,
          orientation: detail.orientation || null,
          pvcFrame: detail.pvcFrame || false,
          glazingGap: detail.glazingGap || null,
          windowArea: {
            value: detail.windowArea.value || null,
            quantity: "m",
          },
          windowTransmissionDetails: {
            dataSource: Number(detail.windowTransmissionDetails?.dataSource) || null, 
            uValue: detail.windowTransmissionDetails?.uValue || 0,
            solarTransmittance: detail.windowTransmissionDetails?.solarTransmittance || 0,
          },
        };
      });

      output.unshift(openingMeasurementsRow);
    }
    this.activeModal.close(this.openingsMeasurementWindowsDetails); // Emitting the current window details when closing the modal
  }
}